import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { useUserContext } from '../../context/UserContext';
import { signInUser } from '../../api_calls/api_calls_auth';

import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { Alert, Snackbar } from '@mui/material';
import Fade from '@mui/material/Fade';
import { alpha, useTheme } from '@mui/material/styles';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { useLanguageContext } from '../../hooks/useLanguageContext';
import { multilingual } from '../../multilingual';

const SignIn = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { login, token } = useUserContext();
    const [formValues, setFormValues] = useState({ username: '', password: '' });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorType, setErrorType] = useState(null);
    const [errors, setErrors] = useState({});
    const [signInSuccess, setSignInSuccess] = useState(false);

    const { language } = useLanguageContext();
    const dictionary = multilingual[language]?.signin ?? multilingual.gr.signin;

    useEffect(() => {
        if (token) {
            navigate('/my-apartments');
        }
    }, [token, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prev) => ({ ...prev, [name]: value }));
        if (value) setErrors((prev) => ({ ...prev, [name]: false }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formValues.username) newErrors.username = true;
        if (!formValues.password) newErrors.password = true;
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClickShowPassword = () => setShowPassword(!showPassword);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowError(false);
        setErrorType(null);

        if (!validate()) {
            setErrorType('fill_fields');
            setShowError(true);
            return;
        }

        setLoading(true);
        try {
            const data = await signInUser(formValues);
            const { token: accessToken } = data;
            const decoded = jwtDecode(accessToken);
            const username = decoded.preferred_username || decoded.username || decoded.email || formValues.username;
            login(accessToken, username);
            setSignInSuccess(true);
            navigate('/my-apartments');
        } catch {
            setErrorType('invalid_credentials');
            setShowError(true);
        } finally {
            setLoading(false);
        }
    };
    const handleCloseSnackbar = () => setSignInSuccess(false);

    return (
        <>
            <CssBaseline />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    minHeight: '100vh',
                    backgroundColor: '#f3f4f6',
                    p: 0,
                }}
            >
                <Box
                    sx={{
                        width: { xs: '0%', md: '50%' },
                        backgroundImage: `url(https://eu-images.contentstack.com/v3/assets/blt31d6b0704ba96e9d/blt88a7087d82cacbf3/655e23392ab1fb0407ec12a9/GettyImages-582763822.jpg?width=850&auto=webp&quality=95&format=jpg&disable=upscale)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: { xs: 'none', md: 'block' },
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.22)} 0%, ${alpha(theme.palette.secondary.main, 0.18)} 100%)`,
                            zIndex: 1,
                        },
                    }}
                >
                    {/* <Box
                        sx={{
                            position: 'absolute',
                            top: 24,
                            left: 24,
                            zIndex: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#fff',
                            fontWeight: 700,
                        }}
                    >
                        <ApartmentIcon sx={{ fontSize: 18 }} />
                        <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
                            Multi-Apartment Building Manager
                        </Typography>
                    </Box> */}
                </Box>
                <Box
                    sx={{
                        width: { xs: '100%', md: '50%' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        px: { xs: 3, sm: 6, md: 8 },
                    }}
                >
                    <Fade in timeout={800}>
                        <Box
                            sx={{
                                width: '100%',
                                maxWidth: 460,
                            }}
                        >
                            <Typography
                                component="h1"
                                variant="h3"
                                sx={{ fontWeight: 800, fontSize: { xs: 36, md: 48 }, lineHeight: 1.05 }}
                            >
                                {dictionary.welcome_back || 'Welcome Back'}
                            </Typography>
                            <Typography sx={{ mt: 1.5, color: '#6b7280', fontSize: 22 }}>
                                {dictionary.signin_subtitle || 'Access your urban portfolio dashboard.'}
                            </Typography>

                            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 5 }}>
                                <Typography sx={{ mb: 1, fontWeight: 700, color: '#374151' }}>
                                    {dictionary.username}
                                </Typography>
                                <TextField
                                    onChange={handleChange}
                                    variant="outlined"
                                    fullWidth
                                    id="username"
                                    type="text"
                                    placeholder={dictionary.username_placeholder || 'e.g. manager@aimestate.com'}
                                    name="username"
                                    autoComplete="username"
                                    autoFocus
                                    error={!!errors.username}
                                    value={formValues.username}
                                    InputProps={{
                                        sx: {
                                            borderRadius: 2,
                                            backgroundColor: '#e5e7eb',
                                        },
                                    }}
                                    sx={{ mb: 3 }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#374151' }}>
                                        {dictionary.password}
                                    </Typography>
                                    <Button
                                        variant="text"
                                        sx={{ p: 0, minWidth: 'auto', textTransform: 'none', fontWeight: 700 }}
                                        onClick={() => navigate('/reset-password')}
                                    >
                                        {dictionary.reset_link_short || 'Forgot Password?'}
                                    </Button>
                                </Box>
                                <TextField
                                    onChange={handleChange}
                                    variant="outlined"
                                    fullWidth
                                    id="password"
                                    type={!showPassword ? 'password' : 'text'}
                                    placeholder="********"
                                    name="password"
                                    autoComplete="current-password"
                                    error={!!errors.password}
                                    value={formValues.password}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword}>
                                                    {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        sx: {
                                            borderRadius: 2,
                                            backgroundColor: '#e5e7eb',
                                        },
                                    }}
                                />
                                {showError && (
                                    <Alert severity="error" sx={{ mt: 2 }}>
                                        {errorType === 'fill_fields'
                                            ? dictionary.error_fill_fields
                                            : dictionary.error_invalid_credentials}
                                    </Alert>
                                )}
                                <Button
                                    sx={{
                                        color: 'white',
                                        mt: 4,
                                        width: '100%',
                                        height: 52,
                                        borderRadius: 2,
                                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                        boxShadow: `0 10px 18px ${alpha(theme.palette.primary.main, 0.25)}`,
                                        textTransform: 'none',
                                        fontWeight: 700,
                                        fontSize: 20,
                                        '&:hover': {
                                            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                        },
                                    }}
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={loading}
                                    endIcon={<ArrowForwardIcon />}
                                >
                                    {loading ? (dictionary.loading || 'Loading...') : (dictionary.signin_button || 'Sign In')}
                                </Button>

                            </Box>
                        </Box>
                    </Fade>
                    <Snackbar open={signInSuccess} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                        <Alert
                            variant="filled"
                            onClose={handleCloseSnackbar}
                            severity="success"
                            sx={{ border: '1px solid', borderColor: 'success.main' }}
                        >
                            {dictionary.success_message}
                        </Alert>
                    </Snackbar>
                </Box>
            </Box >
        </>
    );
};

export default SignIn;
